import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaKey, FaCheck, FaSpinner } from 'react-icons/fa';
import API_URLS from '../apiUrls';

const UserPermissions = ({ darkMode, user }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only fetch permissions if user is logged in
    if (!user) {
      setLoading(false);
      setError('Please log in to view your permissions');
      return;
    }

    const fetchPermissions = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await axios.get(API_URLS.USER_CATEGORIES, {
          withCredentials: true // Ensure cookies are sent
        });
        setPermissions(response.data);
      } catch (err) {
        if (err.response?.status === 401) {
          setError('Please log in to view your permissions');
        } else {
          setError('Unable to load your permissions');
        }
        console.error('Error fetching user permissions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [user]);

  const getCategoryBgColor = category => {
    const colors = [
      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
      'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
      'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    ];
    const index = category.length % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-center py-4">
          <FaSpinner className="animate-spin h-5 w-5 text-purple-500 mr-3" />
          <span className="text-gray-600 dark:text-gray-400">Loading permissions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4">
        <div className="text-center py-4">
          <FaKey className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3">
        <div className="flex items-center">
          <div className="p-1.5 bg-white/20 rounded-lg">
            <FaKey className="h-4 w-4 text-white" />
          </div>
          <div className="ml-2">
            <h3 className="text-base font-semibold text-white">Your Category Permissions</h3>
            <p className="text-purple-100 text-xs">Categories you can access</p>
          </div>
        </div>
      </div>

      <div className="p-4">
        {permissions.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              You have access to the following categories:
            </p>
            <div className="flex flex-wrap gap-2">
              {permissions.map((category, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg ${getCategoryBgColor(category)}`}
                >
                  <FaCheck className="h-3 w-3" />
                  <span className="text-sm font-medium">{category}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <FaKey className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No category permissions granted yet. Contact an administrator to get access.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPermissions; 